import React from 'react';
import { Link } from 'react-router-dom';
import Tooltip from '@/components/atoms/Tooltip';
import { FiEye } from 'react-icons/fi';
import { FaUndo } from 'react-icons/fa';
import { type ColumnDef } from '@/components/organism/Table';
import { Cell, PillCell, AppButton } from '@/components/atoms';
import {
  PROVIDER_CONSTANT_PROPS,
  PROVIDER_COLUMNS,
} from '@/constant/provider/provider';
import { tid } from '@/utils/testId';
import { PROVIDER_TEST_IDS as TID } from '@/constant/testIds/provider.testIds';
import { getDateTime, getDateTimeAgo } from '@/utils/dateTime';
import type { Provider } from '@/store/types';

/* ── Column definitions ───────────────────────────────────────────────────── */
export function buildColumns(
  isCopyRef: React.MutableRefObject<string>,
  onCopyClick: (key: string) => void,
  onActivateClick: (item: Provider) => void,
  isAdmin: boolean,
): ColumnDef<Provider>[] {
  // Adapt ref-based isCopy to work with Cell's string-based copy config
  const makeCopy = (copyKey: string) => ({
    copyKey,
    get isCopy() {
      return isCopyRef.current;
    },
    onCopyClick,
  });

  return [
    {
      accessor: 'profile',
      header: PROVIDER_COLUMNS.PROFILE,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_PROFILE(`${idx}`))}
          value={item.profile}
          tooltipTitle={PROVIDER_COLUMNS.TOOLTIPS.PROFILE}
          tooltipValue={item.name}
          variant="primary"
          copy={makeCopy(`profile-${item.providerId}`)}
        />
      ),
    },
    {
      accessor: 'partnerId',
      header: PROVIDER_COLUMNS.PARTNER_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_PARTNER_ID(`${idx}`))}
          value={item?.partnerId}
          size="text-[11px]"
          copy={makeCopy(`partnerId-${item?.partnerId}`)}
        />
      ),
    },
    {
      accessor: 'server',
      header: PROVIDER_COLUMNS.SERVER,
      sorting: true,
      render: (item, idx) =>
        item.server ? (
          <PillCell
            testId={tid(TID.COL_SERVER(`${idx}`))}
            value={item.server}
            preset="subapp"
            dot
            className="uppercase tracking-widest border font-bold"
          />
        ) : (
          <span className="text-gray-400 dark:text-gray-600 text-[11px]">
            —
          </span>
        ),
    },
    {
      accessor: 'providerId',
      header: PROVIDER_COLUMNS.PROVIDER_ID,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_PROVIDER_ID(`${idx}`))}
          value={item.providerId}
          tooltip
          variant="mono"
          size="text-[11px]"
          copy={makeCopy(`pid-${item.providerId}`)}
        />
      ),
    },
    {
      accessor: 'vdiVersion',
      header: PROVIDER_COLUMNS.VDI_VERSION,
      sorting: true,
      render: (item, idx) => (
        <PillCell
          testId={tid(TID.COL_VDI_VERSION(`${idx}`))}
          value={`v${item.vdiVersion || '0'}`}
          config={{
            bg: 'bg-blue-50 dark:bg-blue-900/20',
            text: 'text-blue-700 dark:text-blue-400',
          }}
          shape="badge"
          size="text-[11px]"
          className="font-bold"
        />
      ),
    },
    {
      accessor: 'syncVdi',
      header: PROVIDER_COLUMNS.SYNC_VDI,
      render: (item, idx) => (
        <PillCell
          testId={tid(TID.COL_SYNC_VDI(`${idx}`))}
          value={item.syncVdi ? 'Synced' : 'Not Synced'}
          preset={item.syncVdi ? 'active' : 'inactive'}
          size="text-[11px]"
          className="border font-semibold"
        />
      ),
    },
    {
      accessor: 'updatedOn',
      header: PROVIDER_COLUMNS.UPDATED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_UPDATED_ON(`${idx}`))}
          value={item.updatedOn}
          variant="muted"
          tooltip={getDateTime(item.updatedOn)}
          tooltipPlacement="top"
        >
          {item.updatedOn ? getDateTimeAgo(item.updatedOn) : '—'}
        </Cell>
      ),
    },
    {
      accessor: 'createdOn',
      header: PROVIDER_COLUMNS.CREATED_ON,
      sorting: true,
      render: (item, idx) => (
        <Cell
          testId={tid(TID.COL_CREATED_ON(`${idx}`))}
          value={item.createdOn}
          format="datetime"
          variant="muted"
        />
      ),
    },
    {
      accessor: 'action',
      header: PROVIDER_COLUMNS.ACTION,
      render: (item, idx) => {
        if (item?.deletedOn) {
          return (
            <Tooltip
              content={isAdmin ? PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.ACTIVATE_MODAL.CONFIRM_LABEL : PROVIDER_CONSTANT_PROPS.CONFIRMATION_MODALS.ACTIVATE_MODAL.TOOLTIP_FOR_NON_ADMINS}
              placement="top"
            >
              <span>
                <AppButton
                  data-testid={tid(TID.COL_REACTIVATE_BTN(`${idx}`))}
                  variant="success"
                  tint
                  shape="circle"
                  size="xs"
                  hoverBg={false}
                  disabled={!isAdmin}
                  onClick={() => isAdmin && onActivateClick(item)}
                  icon={<FaUndo className="h-3.5 w-3.5 rotate-[-90deg]" />}
                />
              </span>
            </Tooltip>
          );
        }

        return (
          <Tooltip content="View Provider" placement="top">
            <Link
              to={`/providers/${item.providerId}`}
              state={item}
              onClick={() =>
                localStorage.setItem(
                  PROVIDER_CONSTANT_PROPS.SELECTED_PROVIDER_ID,
                  item?.providerId || ''
                )
              }
            >
              <AppButton
                data-testid={tid(TID.COL_VIEW_BTN(`${idx}`))}
                variant='info'
                shape='circle'
                size='xs'
                hoverBg={false}
                icon={<FiEye className="h-3.5 w-3.5" />}
              />
            </Link>
          </Tooltip>
        );
      },
    },
  ];
}
